import { useMemo, useState } from "react";

// Shared experiment card — used by both the paid /reports/:token page AND
// the free teaser on /connected, so users see the exact same UI/UX in both
// places. Keeps formatting helpers co-located so nothing drifts.

function fmtInt(n) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}
function fmtPct(n) {
  if (n == null || isNaN(n)) return "—";
  return `${(n * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
}
function fmt(num) {
  if (num == null) return "—";
  if (typeof num !== "number") return String(num);
  if (Math.abs(num) >= 1000) return num.toLocaleString();
  if (num > 0 && num < 1) return `${(num * 100).toFixed(1)}%`;
  return String(num);
}

function TrackBadge({ track }) {
  const colors = {
    "ctr-fix":         "bg-amber-50 text-amber-800 border-amber-200",
    "new-page":        "bg-indigo-50 text-indigo-800 border-indigo-200",
    "content-refresh": "bg-teal-50 text-teal-800 border-teal-200",
    "protect":         "bg-rose-50 text-rose-800 border-rose-200",
    "conversion":      "bg-purple-50 text-purple-800 border-purple-200",
  };
  const cls = colors[track] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border uppercase tracking-wide ${cls}`}>{track}</span>;
}

function ScoreBar({ score }) {
  const color =
    score >= 88 ? "bg-emerald-500" :
    score >= 70 ? "bg-indigo-500" :
                  "bg-slate-400";
  return (
    <div className="flex items-center gap-3">
      <span className={`text-3xl font-extrabold tabular-nums ${score >= 88 ? "text-emerald-600" : score >= 70 ? "text-indigo-600" : "text-slate-600"}`}>
        {score}
      </span>
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden min-w-[80px]">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
      </div>
      <span className="text-[10px] text-slate-400 uppercase tracking-wide">/100</span>
    </div>
  );
}

function GscSignalLine({ signal }) {
  if (!signal) return null;
  const parts = [];
  if (signal.impressions != null) parts.push(<><strong className="text-slate-900">{fmtInt(signal.impressions)}</strong> imp/90d</>);
  if (signal.clicks != null)      parts.push(<><strong className="text-slate-900">{fmtInt(signal.clicks)}</strong> clicks</>);
  if (signal.ctr != null)         parts.push(<>CTR <strong className="text-slate-900">{fmtPct(signal.ctr)}</strong></>);
  if (signal.position != null)    parts.push(<>pos <strong className="text-slate-900">{Number(signal.position).toFixed(1)}</strong></>);
  return (
    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
      {parts.map((p, i) => (
        <span key={i} className="inline-flex items-center">{i > 0 && <span className="text-slate-300 mr-3">·</span>}{p}</span>
      ))}
    </div>
  );
}

function CopyButton({ text, label = "Copy for Cursor" }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
      }}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-[11px] font-bold cursor-pointer transition-colors ${
        copied ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {copied ? "✓ Copied" : `⎘ ${label}`}
    </button>
  );
}

function TodoChecklist({ items, storageKey }) {
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch { return {}; }
  });
  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
  };
  return (
    <ul className="space-y-1.5">
      {items.map((t, i) => {
        const done = !!checked[i];
        return (
          <li key={i} className="flex items-start gap-2 text-sm">
            <button
              onClick={() => toggle(i)}
              aria-label={done ? "Mark as not done" : "Mark as done"}
              className={`mt-[3px] w-4 h-4 rounded border flex items-center justify-center text-[10px] font-bold cursor-pointer flex-shrink-0 ${
                done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 bg-white text-transparent hover:border-slate-500"
              }`}
            >
              ✓
            </button>
            <span className={done ? "text-slate-400 line-through" : "text-slate-700"}>{t}</span>
          </li>
        );
      })}
    </ul>
  );
}

function buildCursorPrompt(exp) {
  return [
    `Experiment: ${exp.title || exp.id}`,
    exp.url ? `URL: ${exp.url}` : null,
    `Track: ${exp.track} · Score: ${exp.score}/100`,
    "",
    `ACTION:`,
    exp.action || "",
    "",
    exp.impact ? `EXPECTED IMPACT:\n${exp.impact}\n` : null,
    exp.proposed_changes?.title ? `NEW TITLE:\n${exp.proposed_changes.title}\n` : null,
    exp.proposed_changes?.meta  ? `NEW META:\n${exp.proposed_changes.meta}\n`  : null,
    exp.todo?.length ? "TODO:\n" + exp.todo.map((t, i) => `${i + 1}. ${t}`).join("\n") + "\n" : null,
    exp.claude_prompt ? `CONTEXT / IMPLEMENTATION NOTES:\n${exp.claude_prompt}` : null,
  ].filter(Boolean).join("\n");
}

export default function ExperimentCard({ exp, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const cursorPrompt = useMemo(() => buildCursorPrompt(exp), [exp]);
  const storageKey = `exp-todo-${exp.id || exp.url || exp.title}`;

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${exp.priority === "high" ? "border-l-4 border-l-red-500 border-slate-200" : "border-slate-200"}`}>
      {/* Compact clickable header — always visible */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 p-4 hover:bg-slate-50 cursor-pointer text-left transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <TrackBadge track={exp.track} />
            {exp.priority === "high" && (
              <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                High priority
              </span>
            )}
            {exp.score != null && (
              <span className={`text-xs font-extrabold px-2 py-0.5 rounded tabular-nums ${
                exp.score >= 88 ? "bg-emerald-100 text-emerald-800" :
                exp.score >= 70 ? "bg-indigo-100 text-indigo-800" :
                                  "bg-slate-100 text-slate-700"
              }`}>
                {exp.score}
              </span>
            )}
          </div>
          <div className="text-sm font-extrabold text-slate-900 leading-snug">{exp.title || exp.url}</div>
          {exp.url && (
            <div className="text-[11px] font-mono text-indigo-600 mt-0.5 break-all">{exp.url}</div>
          )}
          {!open && exp.action && (
            <div className="text-xs text-slate-600 mt-1.5 line-clamp-1">
              <span className="font-semibold text-slate-500">Do:</span> {exp.action}
            </div>
          )}
        </div>
        <svg
          width="16" height="16" viewBox="0 0 16 16" fill="none"
          className={`shrink-0 mt-1 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Expanded body */}
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-100">
          {exp.score != null && (
            <div className="mb-4 pb-4 border-b border-slate-100">
              <ScoreBar score={exp.score} />
            </div>
          )}

          {exp.gsc_signal && (
            <div className="mb-4">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">GSC Signal</div>
              <GscSignalLine signal={exp.gsc_signal} />
            </div>
          )}

          {exp.action && (
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Action required</div>
              <div className="text-sm text-slate-900 font-semibold">{exp.action}</div>
            </div>
          )}

          {exp.impact && (
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Expected impact</div>
              <div className="text-sm font-extrabold text-emerald-700">{exp.impact}</div>
            </div>
          )}

          {exp.todo?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Checklist · {exp.todo.length} step{exp.todo.length === 1 ? "" : "s"}
                </div>
                <CopyButton text={cursorPrompt} />
              </div>
              <TodoChecklist items={exp.todo} storageKey={storageKey} />
            </div>
          )}

        <div className="mt-4 border-t border-slate-100 pt-3 space-y-4 text-sm">
          {exp.proposed_changes?.title && (
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">New title</div>
              <div className="text-slate-900 mt-0.5">{exp.proposed_changes.title}</div>
            </div>
          )}
          {exp.proposed_changes?.meta && (
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">New meta</div>
              <div className="text-slate-700 mt-0.5">{exp.proposed_changes.meta}</div>
            </div>
          )}
          {exp.reasoning?.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Why</div>
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                {exp.reasoning.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {exp.target_queries?.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-1">Target queries</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left pr-2">Query</th>
                      <th className="text-right px-2">Imp</th>
                      <th className="text-right px-2">CTR</th>
                      <th className="text-right pl-2">Target</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-800">
                    {exp.target_queries.map((q, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1 pr-2 truncate max-w-[300px]">{q.query}</td>
                        <td className="text-right px-2">{fmt(q.impressions)}</td>
                        <td className="text-right px-2">{fmt(q.current_ctr)}</td>
                        <td className="text-right pl-2">{fmt(q.target_ctr_min)}–{fmt(q.target_ctr_max)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {exp.proposed_changes?.faq_additions?.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-1">FAQ additions</div>
              <ul className="space-y-1.5 text-slate-700">
                {exp.proposed_changes.faq_additions.map((f, i) => (
                  <li key={i}><span className="font-semibold">Q:</span> {f.q}<br /><span className="text-slate-500">A:</span> <span className="text-slate-600">{f.a}</span></li>
                ))}
              </ul>
            </div>
          )}
          {exp.claude_prompt && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-0.5">Paste into Claude Code</div>
                <div className="text-xs text-slate-600">Ready-to-paste prompt that implements this experiment in your repo.</div>
              </div>
              <CopyButton text={exp.claude_prompt} label="Copy prompt" />
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
